import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ScienceIcon from '@mui/icons-material/Science';
import type {
  ExperimentDocument,
  ExperimentInputPayload,
  ExperimentState,
} from '../experimentTypes';
import type { FunnelABTestDecisionRow } from '../types';
import { useExperiments } from '../hooks/useExperiments';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import { ExperimentsApiClient } from '../api/experimentsClient';
import { ExperimentCard } from '../components/ExperimentCard';
import { HypothesisForm, type AbTestOption } from '../components/HypothesisForm';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import { isActiveDeliveryOnlyExperiment } from '../experimentSurface';
import { loadAbTestOptions } from '../abTestOptionsLoader';
import {
  clearCreativeDecisionHandoffParams,
  creativeDecisionDraftFromSearch,
} from '../creativeDecisionExperimentHandoff';
import { PricingPlannerSection } from '../components/pricing/PricingPlannerSection';
import { parseRowPrefillParams, ROW_PREFILL_PARAMS } from '../rowServedPrefill';

/** Iteration A (AC-801): fixed decision-report window for hub cards. */
const DECISION_WINDOW_DAYS = 30;

const STATE_FILTERS: Array<{ value: 'all' | ExperimentState; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'running', label: 'Running' },
  { value: 'ready_to_decide', label: 'Ready to decide' },
  { value: 'decided', label: 'Decided' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'archived', label: 'Archived' },
];

/** FU part G (D-F4): window presets for the manual research-report dialog. */
const GENERATE_REPORT_PRESETS = [
  { id: 'last-14', label: 'Last 14 days', days: 14 },
  { id: 'last-28', label: 'Last 28 days', days: 28 },
  { id: 'custom', label: 'Custom', days: null },
] as const;

type GenerateReportPresetId = typeof GENERATE_REPORT_PRESETS[number]['id'];

export const ExperimentsHubPage: React.FC = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { experiments, loading, error, createExperiment, updateExperiment, transitionExperiment, refresh } = useExperiments({
    projectId,
    enabled: Boolean(projectId),
  });

  const [stateFilter, setStateFilter] = useState<'all' | ExperimentState>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExperimentDocument | null>(null);
  const [handoffDraft, setHandoffDraft] = useState<ExperimentInputPayload | null>(null);
  const handoffOpenedRef = React.useRef(false);
  const [userTraitOptions, setUserTraitOptions] = useState<Record<string, string[]> | null>(null);
  const [decisions, setDecisions] = useState<FunnelABTestDecisionRow[] | null>(null);
  const [abTestOptions, setAbTestOptions] = useState<AbTestOption[] | null>(null);
  // FU part G (D-F4): manual research-report generation dialog state.
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generatePreset, setGeneratePreset] = useState<GenerateReportPresetId>('last-14');
  const [generateCustomFrom, setGenerateCustomFrom] = useState('');
  const [generateCustomTo, setGenerateCustomTo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const insightsClient = useMemo(() => new FunnelInsightsApiClient(), []);
  const experimentsClient = useMemo(() => new ExperimentsApiClient(), []);
  const webPlacementService = useMemo(() => new WebPlacementService(), []);

  useEffect(() => {
    if (handoffOpenedRef.current) return;
    const draft = creativeDecisionDraftFromSearch(searchParams);
    if (!draft) return;
    handoffOpenedRef.current = true;
    setEditing(null);
    setHandoffDraft(draft);
    setFormOpen(true);
  }, [searchParams]);

  const generateWindow = (): { from: string; to: string } | null => {
    const preset = GENERATE_REPORT_PRESETS.find(entry => entry.id === generatePreset);
    if (preset?.days) {
      const to = new Date();
      const from = new Date(to.getTime() - preset.days * 24 * 60 * 60 * 1000);
      return { from: from.toISOString(), to: to.toISOString() };
    }
    if (!generateCustomFrom || !generateCustomTo) return null;
    return {
      from: new Date(`${generateCustomFrom}:00.000Z`).toISOString(),
      to: new Date(`${generateCustomTo}:00.000Z`).toISOString(),
    };
  };

  const handleGenerateReport = async () => {
    const window = generateWindow();
    if (!window) {
      setGenerateError('Pick a From/To window for the custom range.');
      return;
    }
    setGenerating(true);
    setGenerateError(null);
    try {
      // Synchronous by design — the deterministic engine takes seconds and the
      // response means the card is already upserted (no fake-done).
      await experimentsClient.generateResearchReport(projectId, window);
      setGenerateOpen(false);
      refresh();
    } catch (generationError) {
      setGenerateError(generationError instanceof Error ? generationError.message : 'Research report failed');
    } finally {
      setGenerating(false);
    }
  };

  // Iteration D (AC-1108): real A/B tests for the hypothesis-form picker.
  // Only an exhausted retry chain degrades the form to its free-text fallback;
  // an empty project still gets the picker. Never blocks the hub.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    loadAbTestOptions(webPlacementService, projectId)
      .then(options => {
        if (cancelled) return;
        setAbTestOptions(options);
      });
    return () => { cancelled = true; };
  }, [webPlacementService, projectId]);

  useEffect(() => {
    if (!projectId) return;
    const controller = new AbortController();
    insightsClient.getUserTraits({ projectId, signal: controller.signal })
      .then(response => {
        setUserTraitOptions(Object.fromEntries(
          response.data.traits.map(trait => [trait.key, trait.values.map(entry => entry.value)]),
        ));
      })
      .catch(() => setUserTraitOptions(null));
    return () => controller.abort();
  }, [insightsClient, projectId]);

  // AC-801: ONE decision-report fetch per hub load (fixed 30-day window);
  // cards join by abTestRef. Missing report → cards degrade to 'collecting'.
  useEffect(() => {
    if (!projectId) return;
    const controller = new AbortController();
    const to = new Date();
    const from = new Date(to.getTime() - DECISION_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    insightsClient.getABTestDecisionReport({
      projectId,
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
      signal: controller.signal,
    })
      .then(response => setDecisions(response.data.decisions))
      .catch(() => setDecisions(null));
    return () => controller.abort();
  }, [insightsClient, projectId]);

  const visible = experiments
    .filter(experiment => stateFilter === 'all'
      ? experiment.state !== 'archived'
      : experiment.state === stateFilter)
    .filter(experiment => !isActiveDeliveryOnlyExperiment(experiment, decisions))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  const activeTab: 'experiments' | 'pricing' = searchParams.get('tab') === 'pricing' ? 'pricing' : 'experiments';
  // Phase 2-min (AC-038): S13 row seeds arrive via the URL — memoized on the
  // RAW strings so a tab switch (new URLSearchParams identity) does not
  // re-trigger the calculator's prefill effect.
  const rowSeedRaw = searchParams.get(ROW_PREFILL_PARAMS.seed);
  const rowScopeRaw = [
    searchParams.get(ROW_PREFILL_PARAMS.onboardingId),
    searchParams.get(ROW_PREFILL_PARAMS.versionId),
    searchParams.get(ROW_PREFILL_PARAMS.from),
    searchParams.get(ROW_PREFILL_PARAMS.to),
  ].join('|');
  const rowPrefill = useMemo(
    () => (rowSeedRaw ? parseRowPrefillParams(searchParams) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the raw strings, not the params object identity
    [rowSeedRaw, rowScopeRaw],
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }} data-testid={EXPERIMENTS_TEST_IDS.page}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Experiments</Typography>
          <Typography variant="body2" color="text.secondary">
            One registry for A/B, pricing, and discovery experiments — with pre-registered hypotheses and a full lifecycle audit.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={stateFilter}
              data-testid={EXPERIMENTS_TEST_IDS.statusFilter}
              onChange={(event) => setStateFilter(event.target.value as 'all' | ExperimentState)}
            >
              {STATE_FILTERS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<ScienceIcon />}
            data-testid={EXPERIMENTS_TEST_IDS.generateResearchReport}
            onClick={() => {
              setGenerateError(null);
              setGeneratePreset('last-14');
              setGenerateOpen(true);
            }}
          >
            Generate research report
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            data-testid={EXPERIMENTS_TEST_IDS.createButton}
            onClick={() => { setEditing(null); setHandoffDraft(null); setFormOpen(true); }}
          >
            New experiment
          </Button>
        </Stack>
      </Stack>

      {/* FU part G (D-F4): manual research-report window dialog. */}
      <Dialog
        open={generateOpen}
        onClose={() => { if (!generating) setGenerateOpen(false); }}
        maxWidth="xs"
        fullWidth
        data-testid={EXPERIMENTS_TEST_IDS.generateReportDialog}
      >
        <DialogTitle>Generate research report</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Deterministic warehouse report (section catalog A–K) for the picked window. Runs in seconds — the research card updates when it finishes.
            </Typography>
            <Stack direction="row" spacing={1}>
              {GENERATE_REPORT_PRESETS.map(preset => (
                <Chip
                  key={preset.id}
                  label={preset.label}
                  color={generatePreset === preset.id ? 'primary' : 'default'}
                  variant={generatePreset === preset.id ? 'filled' : 'outlined'}
                  data-testid={EXPERIMENTS_TEST_IDS.generateReportPreset(preset.id)}
                  onClick={() => setGeneratePreset(preset.id)}
                  disabled={generating}
                />
              ))}
            </Stack>
            {generatePreset === 'custom' && (
              <Stack direction="row" spacing={1.5}>
                <TextField
                  label="From (UTC)"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={generateCustomFrom}
                  onChange={event => setGenerateCustomFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={generating}
                />
                <TextField
                  label="To (UTC)"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={generateCustomTo}
                  onChange={event => setGenerateCustomTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={generating}
                />
              </Stack>
            )}
            {generateError && (
              <Alert severity="error" data-testid={EXPERIMENTS_TEST_IDS.generateReportError}>
                {generateError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generating}>Cancel</Button>
          <Button
            variant="contained"
            data-testid={EXPERIMENTS_TEST_IDS.generateReportConfirm}
            onClick={() => void handleGenerateReport()}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : undefined}
          >
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Pricing planner (plan 2026-08-10 §14.2): one registry, two views. */}
      <Tabs
        value={activeTab}
        onChange={(_, value: 'experiments' | 'pricing') => {
          const next = new URLSearchParams(searchParams);
          if (value === 'pricing') next.set('tab', 'pricing');
          else next.delete('tab');
          setSearchParams(next, { replace: true });
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        data-testid={EXPERIMENTS_TEST_IDS.hubTabs}
      >
        <Tab value="experiments" label="All experiments" data-testid={EXPERIMENTS_TEST_IDS.hubTabExperiments} />
        <Tab value="pricing" label="Pricing" data-testid={EXPERIMENTS_TEST_IDS.hubTabPricing} />
      </Tabs>

      {activeTab === 'pricing' ? (
        <PricingPlannerSection
          projectId={projectId}
          rowPrefill={rowPrefill}
          experiments={experiments}
          experimentsLoading={loading}
          decisions={decisions}
          onCreate={async (input) => { await createExperiment(input); }}
          onTransition={transitionExperiment}
          onEdit={(target) => { setEditing(target); setFormOpen(true); }}
          onResearchGenerated={refresh}
        />
      ) : (
      <Stack spacing={1.5} data-testid={EXPERIMENTS_TEST_IDS.list}>
        {!loading && visible.length === 0 && (
          <Alert severity="info" data-testid={EXPERIMENTS_TEST_IDS.emptyState}>
            No experiments in this view yet. Create one and pre-register its hypothesis before running.
          </Alert>
        )}
        {visible.map(experiment => (
          <ExperimentCard
            key={experiment.id}
            projectId={projectId}
            experiment={experiment}
            decisions={decisions}
            onTransition={transitionExperiment}
            onEdit={(target) => { setEditing(target); setFormOpen(true); }}
            onResearchGenerated={refresh}
          />
        ))}
      </Stack>
      )}

      <HypothesisForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          if (handoffDraft) {
            setHandoffDraft(null);
            setSearchParams(clearCreativeDecisionHandoffParams(searchParams), { replace: true });
          }
        }}
        projectId={projectId}
        initial={editing}
        initialDraft={handoffDraft}
        userTraitOptions={userTraitOptions}
        abTestOptions={abTestOptions}
        onSubmit={async (input) => {
          if (editing) {
            await updateExperiment(editing.id, input);
          } else {
            await createExperiment(input);
            if (handoffDraft) {
              setHandoffDraft(null);
              setSearchParams(clearCreativeDecisionHandoffParams(searchParams), { replace: true });
            }
          }
        }}
      />
    </Container>
  );
};

export default ExperimentsHubPage;
